import * as React from 'react';
import { CTCIPublisher } from '../utils/metrics';
import { Components } from '../constants';

declare global {
  interface Window {
    AmazonTakt?: {
      app_id: string;
      visit_metadata: {
        pageType: string;
      };
      metrics?: {
        tti?: number;
      };
      componentReady: {
        timing: {
          [name: string]: number;
        };
      };
    };
  }
}

const MAX_RETRIES = 10;
const INTERVAL_TIME = 1000;

const emitMetrics = ({
  isMobile,
  experiments,
  appName,
  pageType,
  ctci,
  componentName,
}: {
  isMobile: boolean;
  experiments?: Map<string, boolean>;
  appName?: string;
  pageType?: string;
  ctci: number;
  componentName?: string;
}) => {
  let experimentsKey = '';
  experiments?.forEach((isEnabled, experiment) => {
    if (isEnabled) {
      experimentsKey += '-' + experiment;
    }
  });
  const componentNameString = componentName ? `-${componentName}` : '';
  const key = `${
    isMobile ? 'mobile' : 'desktop'
  }-${appName}-${pageType}${experimentsKey}${componentNameString}`;
  CTCIPublisher.publishTimerMonitor(key, ctci);
};

const emitPageAndComponentLevelMetrics = ({
  isMobile,
  experiments,
}: {
  isMobile: boolean;
  experiments?: Map<string, boolean>;
}): void => {
  const amazonTakt = window.AmazonTakt;
  const appName = amazonTakt?.app_id;
  const pageType = amazonTakt?.visit_metadata.pageType;

  emitMetrics({
    isMobile,
    experiments,
    appName: appName,
    pageType: pageType,
    ctci: amazonTakt?.metrics?.tti || 0,
  });

  Object.values(Components).forEach((component) => {
    if (
      !amazonTakt?.componentReady?.timing?.READY?.hasOwnProperty(component) ||
      !amazonTakt?.componentReady?.timing?.START?.hasOwnProperty(component)
    ) {
      return;
    }

    emitMetrics({
      isMobile,
      experiments,
      appName: appName,
      pageType: pageType,
      ctci:
        amazonTakt.componentReady.timing.READY[component] -
        amazonTakt.componentReady.timing.START[component],
      componentName: component,
    });
  });
};

export default ({
  isMobile,
  experiments,
}: {
  isMobile: boolean;
  experiments?: Map<string, boolean>;
}): void => {
  React.useEffect(() => {
    let retries = 0;
    let interval;

    const removeInterval = () => {
      if (interval) {
        clearInterval(interval);
      }
      interval = undefined;
    };

    // attempt to log CTCI on load
    if (window.AmazonTakt?.metrics?.tti) {
      emitPageAndComponentLevelMetrics({
        isMobile,
        experiments,
      });
    } else {
      // did not log metric on load, will retry until we succeed for a maximum of 10 seconds
      interval = setInterval(() => {
        if (retries >= MAX_RETRIES) {
          removeInterval();
          return;
        }
        if (window.AmazonTakt?.metrics?.tti) {
          emitPageAndComponentLevelMetrics({
            isMobile,
            experiments,
          });
          removeInterval();
          return;
        }
        retries += 1;
      }, INTERVAL_TIME);
    }

    return () => {
      removeInterval();
    };
  }, []);
};
